import { useCallback, useMemo } from 'react';
import { OptionWithId, SELECT_ALL_OPTION, SelectAllOption } from './types';

type UseSelectAllProps<OptionType extends OptionWithId> = {
  options: OptionType[];
  value: OptionType[];
  onChange: (newValue: OptionType[]) => void;
  showSelectAll: boolean;
};

export const useSelectAll = <OptionType extends OptionWithId>({
  options,
  value,
  onChange,
  showSelectAll,
}: UseSelectAllProps<OptionType>) => {
  const allItemsSelected = useMemo(
    () => options.length > 0 && value.length === options.length,
    [options.length, value.length],
  );

  const optionsWithSelectAll = useMemo(
    () => (showSelectAll ? [SELECT_ALL_OPTION, ...options] : options),
    [showSelectAll, options],
  );

  const handleChange = useCallback(
    (_: unknown, newValue: (OptionType | SelectAllOption)[]) => {
      const hasSelectAll = newValue.some((v) => v.id === SELECT_ALL_OPTION.id);

      if (hasSelectAll && !allItemsSelected) {
        onChange(options);
        return;
      }

      if (!hasSelectAll && allItemsSelected) {
        onChange([]);
        return;
      }

      const filteredValue = newValue.filter((v): v is OptionType => v.id !== SELECT_ALL_OPTION.id);
      onChange(filteredValue);
    },
    [allItemsSelected, options, onChange],
  );

  const displayValue = useMemo(
    () => (showSelectAll && allItemsSelected ? [SELECT_ALL_OPTION, ...value] : value),
    [showSelectAll, allItemsSelected, value],
  );

  return {
    allItemsSelected,
    optionsWithSelectAll,
    handleChange,
    displayValue,
  };
};
